import {
  MerchantPaymentProduct,
  TermsIntervals,
  PaymentTypes,
  PaymentType,
} from '@ads-bread/shared/bread/codecs';
import {
  Duration,
  DURATION_DESIGNATORS,
  DurationDesignator,
  ComputedPaymentTypeValues,
  PromoOffer,
} from './types';
import {
  DEFAULT_MAX_CART_SIZE,
  EXAMPLE_TOTAL_PRICE,
} from './constants/promo-offer';
import {
  calculateStandardPayment,
  rateBPStoRateDecimal,
} from './payment-calculations';

export type PaymentTermsStruct = Omit<PromoOffer, 'paymentAmount'>;

export const DESIGNATOR_MAPPING: Record<
  string,
  Record<string, (length?: number) => string>
> = {
  M: {
    singular: () => 'mo',
    plural: (length) => `${length} months`,
  },
  W: {
    singular: () => 'week',
    plural: (length) => `${length} weeks`,
  },
};

function isDurationDesignator(
  designator: string
): designator is DurationDesignator {
  return (DURATION_DESIGNATORS as readonly string[]).includes(designator);
}

/**
 * Determines if a payment type is valid.
 *
 * @param type - A payment type as a string.
 *
 * @returns - Returns true if the payment type is valid.
 *
 */
export function isPaymentType(type: string): boolean {
  return Object.keys(PaymentTypes).includes(type);
}

/**
 * Formats a frequency as singular or plural  .
 *
 * @param duration - The frequency.
 *
 * @returns - Returns frequency as singular or plural.
 *
 */
export function frequencyString(duration: Duration): string {
  const singular = duration.length === 1 ? 'singular' : 'plural';
  const singularCall = DESIGNATOR_MAPPING?.[duration.designator]?.[singular];
  if (!singularCall) {
    throw new Error('No singularCall.');
  }
  return singularCall(duration.length);
}

/**
 * Parses a duration from frequency string.
 *
 * @param frequency - Frequency as a string.
 *
 * @returns - Returns duration from frequency string.
 *
 */
export function parseDuration(frequency: string): Duration {
  const durationRegex = /P(?<length>\d+)(?<designator>[A-Z])/;
  const match = durationRegex.exec(frequency);
  const groups = match?.groups;
  if (!groups || !groups.length || !groups.designator) {
    throw new Error('Unable to parse ISO8601 Duration');
  }
  if (!isDurationDesignator(groups.designator)) {
    throw new Error(
      `Duration designator must be M (month) or W (week). Received: ${groups.designator}`
    );
  }
  return { length: parseInt(groups.length), designator: groups.designator };
}

/**
 * Converts payment product to payment terms with min interest rate
 *
 * @returns - Returns payment terms.
 *
 */
export function paymentProductToPaymentTermsStruct(): (
  merchantPaymentProduct: MerchantPaymentProduct
) => PaymentTermsStruct {
  return function paymentProductToPayment(
    merchantPaymentProduct: MerchantPaymentProduct
  ) {
    if (!isPaymentType(merchantPaymentProduct.paymentProduct.type)) {
      throw new Error(
        `Payment Product type is not valid: received ${merchantPaymentProduct.paymentProduct.type}`
      );
    }
    const frequency = parseDuration(
      merchantPaymentProduct.paymentProduct.paymentFrequency
    );

    return {
      frequency,
      rate: merchantPaymentProduct.paymentProduct.minInterestRateBPS,
      type: merchantPaymentProduct.paymentProduct.type,
      numberOfPayments: merchantPaymentProduct.paymentProduct.numPayments,
    };
  };
}

/**
 * Converts terms interval to duration.
 *
 * @param interval - Terms interval.
 *
 * @returns - Returns terms interval converted to duration
 *
 */
export function paymentAgreementIntervalToDuration(
  interval: TermsIntervals
): Duration {
  if (interval === 'MONTHLY') {
    return { designator: 'M', length: 1 };
  } else {
    return { designator: 'W', length: 2 };
  }
}

/**
 * Sorts merchant payment products by field from lowest to highest
 * @param product
 * @param field
 * @returns
 */
export const getSortedPaymentProductField = (
  product: MerchantPaymentProduct[],
  field: 'numPayments' | 'minInterestRateBPS' | 'maxInterestRateBPS'
): number[] => {
  return product
    .map(({ paymentProduct }) => paymentProduct[field])
    .sort((a, b) => a - b);
};

/**
 * Sorts merchant values by field from lowest to highest
 * @param product
 * @param field
 * @returns
 */
export const getSortedMerchantValuesField = (
  product: MerchantPaymentProduct[],
  field: 'minCartSize' | 'maxCartSize'
): number[] => {
  return product
    .map(({ merchantValues }) => merchantValues[field].value)
    .sort((a, b) => a - b);
};

/**
 * Computes values for a given payment type on the basis of a passed in
 * PromoOffer and merchantPaymentProducts
 * @param merchantPaymentProducts
 * @param promoOffer PromoOffer
 * @returns ComputedPaymentTypeValues
 */
export function computePaymentTypeValues(
  merchantPaymentProducts: MerchantPaymentProduct[],
  promoOffer?: PromoOffer | Partial<PromoOffer>
): ComputedPaymentTypeValues | null {
  if (!promoOffer) {
    return null;
  }

  const { numberOfPayments, rate, paymentAmount, frequency } =
    promoOffer as PromoOffer;

  const byPromoOfferType = ({ paymentProduct }: MerchantPaymentProduct) =>
    paymentProduct.type === promoOffer.type;

  const paymentProducts = merchantPaymentProducts.filter(byPromoOfferType);

  if (!paymentProducts.length) {
    return null;
  }

  const lowestInterestRate =
    getSortedPaymentProductField(
      paymentProducts,
      'minInterestRateBPS'
    ).shift() ?? 0;

  const sortedInterestRates = getSortedPaymentProductField(
    paymentProducts,
    'maxInterestRateBPS'
  );
  const highestInterestRate = sortedInterestRates.pop() ?? 0;

  const paymentProductsSortedByNumPayments = getSortedPaymentProductField(
    paymentProducts.filter((product) => {
      const designator = parseDuration(product.paymentProduct.paymentFrequency);
      return designator.designator === promoOffer.frequency?.designator;
    }),
    'numPayments'
  );

  const smallestCartSize =
    getSortedMerchantValuesField(paymentProducts, 'minCartSize').shift() ?? 0;

  const sortedMaxCartSizes = getSortedMerchantValuesField(
    paymentProducts.filter((pp) => pp.merchantValues.maxCartSize.value >= 0),
    'maxCartSize'
  );

  const largestCartSize = sortedMaxCartSizes.pop();

  return {
    exampleTotalPrice: EXAMPLE_TOTAL_PRICE.value,
    lengthOptions: paymentProductsSortedByNumPayments,
    minCartSize: smallestCartSize,
    maxCartSize: largestCartSize ?? DEFAULT_MAX_CART_SIZE,
    minInterestRateBPS: lowestInterestRate,
    maxInterestRateBPS: highestInterestRate,
    promoOfferFrequency: frequency,
    promoOfferLength: numberOfPayments,
    promoOfferAmount: paymentAmount,
    promoOfferRate: rate,
  };
}

/**
 * Computes default values for a given payment type on the basis of a passed in
 * merchantPaymentProducts and displayInterestRateBPS.
 * @todo - Should we do using the example promo interest rate for installments and split pay
 * instead of displayInterestRateBPS
 * @param paymentType
 * @param merchantPaymentProducts
 * @param displayInterestRateBPS
 * @returns ComputedPaymentTypeValues
 */
export function computeDefaultMerchantPaymentTypeValues(
  paymentType: PaymentType,
  merchantPaymentProducts: MerchantPaymentProduct[],
  displayInterestRateBPS: number
): ComputedPaymentTypeValues | null {
  const paymentProducts = merchantPaymentProducts
    .filter(({ paymentProduct }) => paymentProduct.type === paymentType)
    .sort(
      (a, b) => a.paymentProduct.numPayments - b.paymentProduct.numPayments
    );

  // To-do: This requirement will only be fulfilled
  // if duration of the payment periods is the same across the payment products.
  // we should compute the term length in days and sort that way.
  const longestPaymentProduct = paymentProducts
    .filter(({ paymentProduct }) => paymentProduct.type === paymentType)
    .sort((a, b) => a.paymentProduct.numPayments - b.paymentProduct.numPayments)
    .pop();
  if (!longestPaymentProduct) {
    return null;
  }

  const { numPayments, paymentFrequency } =
    longestPaymentProduct.paymentProduct;
  const frequency = parseDuration(paymentFrequency);

  const paymentAmount = calculateStandardPayment({
    principal: EXAMPLE_TOTAL_PRICE.value,
    interestRate: rateBPStoRateDecimal(displayInterestRateBPS),
    termLength: numPayments,
    duration: frequency,
  });

  const lowestInterestRate =
    getSortedPaymentProductField(
      paymentProducts,
      'minInterestRateBPS'
    ).shift() ?? 0;

  const sortedInterestRates = getSortedPaymentProductField(
    paymentProducts,
    'maxInterestRateBPS'
  );
  const highestInterestRate = sortedInterestRates.pop() ?? 0;

  const paymentProductsSortedByNumPayments = getSortedPaymentProductField(
    paymentProducts.filter((product) => {
      const designator = parseDuration(product.paymentProduct.paymentFrequency);
      return designator.designator === frequency.designator;
    }),
    'numPayments'
  );

  const smallestCartSize =
    getSortedMerchantValuesField(paymentProducts, 'minCartSize').shift() ?? 0;

  const largestCartSize = getSortedMerchantValuesField(
    paymentProducts.filter((pp) => pp.merchantValues.maxCartSize.value >= 0),
    'maxCartSize'
  ).pop();

  return {
    exampleTotalPrice: EXAMPLE_TOTAL_PRICE.value,
    lengthOptions: paymentProductsSortedByNumPayments,
    minCartSize: smallestCartSize,
    maxCartSize: largestCartSize ?? DEFAULT_MAX_CART_SIZE,
    minInterestRateBPS: lowestInterestRate,
    maxInterestRateBPS: highestInterestRate,
    promoOfferFrequency: frequency,
    promoOfferLength: numPayments,
    promoOfferAmount: paymentAmount,
    promoOfferRate: displayInterestRateBPS,
  };
}
